import { MessageCircle, ChevronDown } from "lucide-react";
import { motion } from "motion/react";
import { useEffect, useState } from "react";

interface HeroSectionProps {
  onOpenChat: () => void;
}

interface ProfilePublic {
  name: string;
  title: string | null;
  elevator_pitch: string | null;
  availability_status: string | null;
  target_titles: string[] | null;
  target_company_stages: string[] | null;
}

// Start fetch at module load — overlaps with React mount instead of waiting
const profilePromise = fetch("/api/profile").then((r) => r.json());

const HeroSection = ({ onOpenChat }: HeroSectionProps) => {
  const [profile, setProfile] = useState<ProfilePublic | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    profilePromise.then((data) => {
      if (data) setProfile(data as ProfilePublic);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <section className="min-h-screen flex flex-col items-center justify-center px-6 pt-16">
        <div className="text-center max-w-3xl animate-pulse space-y-4">
          <div className="h-6 w-48 bg-muted rounded-full mx-auto" />
          <div className="h-16 w-80 bg-muted rounded-lg mx-auto" />
          <div className="h-8 w-64 bg-muted rounded-lg mx-auto" />
          <div className="h-6 w-96 bg-muted rounded-lg mx-auto" />
        </div>
      </section>
    );
  }

  const name = profile?.name || "Tom Hudson";
  const title = profile?.title || "";
  const pitch = profile?.elevator_pitch || "";
  const status = profile?.availability_status || "open";
  const stages = profile?.target_company_stages?.length ? profile.target_company_stages.join("–") : null;
  const titles = profile?.target_titles?.length ? profile.target_titles.join(", ") : null;

  const statusLabel = status === "actively_looking" ? "Actively Looking" : status === "open" ? "Open to Opportunities" : "Not Looking";

  return (
    <section className="min-h-screen flex flex-col items-center justify-center px-6 pt-16 relative">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-center max-w-3xl"
      >
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card text-sm text-muted-foreground mb-8">
          <span className="w-2 h-2 rounded-full bg-primary animate-pulse-glow" />
          {statusLabel}{titles ? ` · ${titles}` : ""}{stages ? ` at ${stages}` : ""}
        </div>

        <h1 className="text-5xl md:text-7xl font-serif font-bold tracking-tight text-foreground mb-4">
          {name}
        </h1>

        <p className="text-xl md:text-2xl text-primary font-medium mb-4">
          {title}
        </p>

        <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
          {pitch}
        </p>

        <button
          onClick={onOpenChat}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:opacity-90 transition-opacity glow-primary"
        >
          <MessageCircle size={18} />
          Ask AI About Me
        </button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8"
      >
        <ChevronDown size={24} className="text-muted-foreground animate-bounce" />
      </motion.div>
    </section>
  );
};

export default HeroSection;
